<template>
    <div>
        <div class="form-group row">
            <label class="col-sm-3 col-form-label">
                {{ $t('visit-information-form.meal_reservation.label_meal_reservation') }}
            </label>

            <div class="col-sm-9 my-auto">
                <div v-for="(item, index) in mealDetails.meal" :key="`${item.meal_type_id}_${item.date}`">
                    <div class="my-2">
                        <label>
                            {{ getMealType(item.meal_type_id) }}
                            {{ $t('visit-information-form.meal_reservation.venue') }}
                            ({{ formatDate(item.date) }})
                        </label>
                        <small class="text-danger">*</small>
                        <div class="my-auto">
                            <select 
                                v-if="!confirm" 
                                v-model="selectedVenues[index]" 
                                class="form-control"
                                @change="updateSelections"
                                :class="{ 'is-invalid': validationErrors[`venue_${index}`] }"
                                >
                                <option v-for="venue in venueOptions" :key="venue.id" :value="venue.id">
                                    {{ $i18n.locale === 'ja' ? venue.name_jp : venue.name_en }}
                                </option>
                            </select>

                            <p v-if="confirm">
                                {{ getVenueName(selectedVenues[index]) }}
                            </p>
                        </div>
                        <div v-if="validationErrors[`venue_${index}`]" class="invalid-feedback d-block">
                        {{ validationErrors[`venue_${index}`] }}
                        </div>
                    </div>

                    <div class="my-4">
                        <label>
                            {{ getMealType(item.meal_type_id) }}
                            {{ $t('visit-information-form.meal_reservation.time') }}
                            ({{ formatDate(item.date) }})
                        </label>
                        <small class="text-danger">*</small>
                        <div class="my-auto">
                            <select 
                                v-if="!confirm" 
                                v-model="selectedTimes[index]" 
                                class="form-control"
                                @change="updateSelections"
                                :class="{ 'is-invalid': validationErrors[`time_${index}`] }"
                                >
                                <option v-for="slot in getTimeSlots(item.meal_type_id, selectedVenues[index])"
                                    :key="slot.start" :value="slot.start">
                                    {{ slot.start }} - {{ slot.end }}
                                </option>
                            </select>

                            <p v-if="confirm">
                                {{ selectedTimes[index] }} -
                                {{ getEndTime(selectedTimes[index], item.meal_type_id, selectedVenues[index]) }}
                            </p>
                        </div>
                        <div v-if="validationErrors[`time_${index}`]" class="invalid-feedback d-block">
                        {{ validationErrors[`time_${index}`] }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'MealComponent',
    props: {
        facility: Object,
        companions: Array,
        mealDetails: Object,
        confirm: Boolean
    },
    data() {
        return {
            showValidations: false,
            venueOptions: [],
            selectedVenues: [],
            selectedTimes: [],
            facilityInfo: [],
            guestCount: 1,
            validationErrors: {} // key: index, value: message
        };
    },
    computed: {
        ...mapState('memorizedInputs', ['checkinFormType']),
        ...mapState('guest', ['numberOfGuest', 'guestCountSetting'])
    },
    async mounted() {
        await this.fetchFacilityInMrs(this.facility.id);
        this.selectedSlots();
    },
    methods: {
        validateSelections() {
            this.validationErrors = {};

            this.mealDetails.meal.forEach((item, index) => {
                if(!this.selectedVenues[index]) {
                    this.validationErrors[`venue_${index}`] = this.$t('visit-information-form.meal_reservation.venue_error');
                }

                if (!this.selectedTimes[index]) {
                    this.validationErrors[`time_${index}`] = this.$t('visit-information-form.meal_reservation.time_error');
                }
            });

             return Object.keys(this.validationErrors).length === 0;
        },

        selectedSlots() {
            const count = this.mealDetails?.meal?.length || 0;
            this.selectedVenues = Array(count).fill(null);
            this.selectedTimes = Array(count).fill(null);
        },

        async fetchFacilityInMrs(facilityId) {
            try {
                const response = await this.$mrsApi.get(`/facility?checkin_facility_id=${facilityId}`);
                this.facilityInfo = response.data;
            
                this.fetchVenues(this.facilityInfo[0]?.id);
            } catch (error) {
                console.error('Error fetching facility info:', error);
            }
        },

        async fetchVenues(id) {
            try {
                const { data } = await this.$mrsApi.get(`/venue?facility_id=${id}`);
                this.venueOptions = data.length ? data : [this.getFallbackVenueOption()];
            } catch (error) {
                console.error('Error fetching venues:', error);
                this.venueOptions = [this.getFallbackVenueOption('error')];
            }
        },

        getFallbackVenueOption(type = 'default') {
            return {
                id: null,
                name_en: type === 'error' ? 'Error fetching venue' : 'Not available (please contact front desk)',
                name_jp: type === 'error' ? '会場の取得中にエラーが発生しました' : this.$t('visit-information-form.meal_reservation.not_available')
            };
        },

        getMealType(mealId) {
            return {
                '1': this.$t('visit-information-form.meal_reservation.breakfast'),
                '2': this.$t('visit-information-form.meal_reservation.lunch'),
                '3': this.$t('visit-information-form.meal_reservation.dinner')
            }[mealId] || this.$t('visit-information-form.meal_reservation.not_available');
        },

        getTimeSlots(mealType, venueId) {
            const venue = this.venueOptions.find(v => v.id === venueId);
            const slots = venue?.details?.time_slots?.[this.mapMealType(mealType)];
            return slots || [{ start: 'N/A', end: 'N/A' }];
        },

        getVenueName(venueId) {
            const venue = this.venueOptions.find(v => v.id === venueId);
            return venue ? (this.$i18n.locale === 'ja' ? venue.name_jp : venue.name_en) : this.$t('visit-information-form.meal_reservation.not_available');
        },

        getGuestCount() {
            return this.checkinFormType === 'rsv' && this.guestCountSetting.fix_guest_count.status
                ? this.numberOfGuest
                : this.companions.length + 1;
        },

        formatDate(date) {
            return date ? date.replace(/-/g, '/') : this.$t('visit-information-form.meal_reservation.not_available');
        },

        getEndTime(startTime, mealType, venueId) {
            const venue = this.venueOptions.find(v => v.id === venueId);
            const slot = venue?.details?.time_slots?.[this.mapMealType(mealType)]?.find(s => s.start === startTime);
            return slot?.end || this.$t('visit-information-form.meal_reservation.not_available');
        },

        mapMealType(typeId) {
            return {
                1: 'breakfast',
                2: 'lunch',
                3: 'dinner'
            }[typeId];
        },

        updateSelections() {
            this.validationErrors = {};

            const selections = this.mealDetails.meal.reduce((acc, item, index) => {
                const mealType = this.mapMealType(item.meal_type_id);
                const venueId = this.selectedVenues[index];
                const startTime = this.selectedTimes[index];

                const venue = this.venueOptions.find(v => v.id === venueId);
                const slot = venue?.details?.time_slots?.[mealType]?.find(s => s.start === startTime);
                const endTime = slot?.end || null;

                if (!acc[item.date]) acc[item.date] = {};
                if (!acc[item.date][mealType]) acc[item.date][mealType] = [];

                this.guestCount = this.getGuestCount();

                acc[item.date][mealType].push({
                    venue_id: venueId,
                    time_slot: startTime ? { start_time: startTime, end_time: endTime } : null,
                    number_of_pax: this.guestCount,
                    menu: item.meal_name
                });

                return acc;
            }, {});

            this.$emit('mealSelection', selections);
        }
    },
    watch: {
        companions: {
            deep: true,
            handler(newVal) {
                this.guestCount = this.getGuestCount();
                this.updateSelections();
                console.log('guest count', this.guestCount);
            }
        },
        '$i18n.locale'(newLocale) {
            this.validateSelections(); // or a separate method to refresh error messages
        }
    }
};
</script>

<style lang="scss" scoped>
    .is-invalid {
        border: 1px solid red;
        background-color: #ffe6e6;
    }
</style>